import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocaleNavigate } from '@/hooks/use-locale-navigate'
import { Search, Palmtree, Tag, Droplets } from 'lucide-react'
import { StreamIcon } from '@swim/ui'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'
import { useMyStreams } from '@/hooks/use-my-streams'
import { useSession } from '@/hooks/use-session'
import { useSearchSuggestions } from '@/hooks/use-search-suggestions'
import { OfferCompactRow } from '@/components/offers/OfferCompactRow'
import type { OfferResult, StreamResult } from '@/types/search'

const MERCHANT_PORTAL_URL = (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_MERCHANT_PORTAL_URL
  ?? 'https://merchant-staging.swimflows.com'

interface CommandPaletteContentProps {
  value: string
  setValue: (v: string) => void
  onClose: () => void
  onSearch: (q: string) => void
  navigate: (path: string) => void
  user: { id: string } | null | undefined
  isMerchant: boolean
  matchedStreams: Array<{ id: string; name: string }>
  hasStreams: boolean
  /** Live catalog matches from GET /search (spec-022, US3) — real Offers/Streams,
      not just this user's own streams or hardcoded nav links. */
  suggestions: { offers: OfferResult[]; streams: StreamResult[] }
  /** Mobile bottom-sheet mode — input renders BELOW the results so it sits
      against the keyboard; results stack upward and scroll independently. */
  inputAtBottom?: boolean
}

function CommandPaletteContent({
  value, setValue, onClose, onSearch, navigate, user, isMerchant, matchedStreams, hasStreams, suggestions, inputAtBottom = false,
}: CommandPaletteContentProps) {
  const { t } = useTranslation('navigation')
  const input = (
    <CommandInput
      placeholder={t('search.placeholder', { ns: 'common' })}
      value={value}
      onValueChange={setValue}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && value.trim()) onSearch(value.trim())
      }}
      className="text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
    />
  )

  return (
    <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-slate-400 dark:[&_[cmdk-group-heading]]:text-slate-500 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]]:border-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
      {!inputAtBottom && input}
      <CommandList className={inputAtBottom
        ? 'max-h-[45dvh] overflow-y-auto border-b border-sand-200 dark:border-slate-700'
        : 'max-h-[60vh] overflow-y-auto'
      }>
        <CommandEmpty className="py-8 text-center text-sm text-slate-400 dark:text-slate-500 font-sans">
          {value.trim()
            ? 'No results — press Enter to search'
            : 'Type to search offers and stores'}
        </CommandEmpty>

        {value.trim() && (
          <CommandGroup heading={t('chrome.search.label')}>
            <CommandItem
              onSelect={() => onSearch(value.trim())}
              className="cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
            >
              <Search className="text-slate-400" />
              <span className="font-sans text-slate-700 dark:text-slate-200">
                {t('chrome.search.searchFor')}"{ value}"
              </span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        )}

        {value.trim() && suggestions.offers.length > 0 && (
          <>
            <CommandSeparator className="bg-sand-200 dark:bg-slate-700" />
            <CommandGroup heading={t('chrome.search.offers', { ns: 'common', defaultValue: 'Offers' })}>
              {suggestions.offers.map((offer) => (
                <CommandItem
                  key={offer.id}
                  value={`offer-${offer.id}-${offer.title}`}
                  onSelect={() => {
                    navigate(`/streams/${offer.merchantSlug}/offers/${offer.id}`)
                    onClose()
                  }}
                  className="px-0 py-1 cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
                >
                  <OfferCompactRow
                    offer={offer}
                    onClick={() => {}} // Let CommandItem handle navigation
                    hideMerchantName
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {value.trim() && suggestions.streams.length > 0 && (
          <>
            <CommandSeparator className="bg-sand-200 dark:bg-slate-700" />
            <CommandGroup heading={t('chrome.search.sellers', { ns: 'common', defaultValue: 'Sellers' })}>
              {suggestions.streams.map((stream) => (
                <CommandItem
                  key={stream.id}
                  onSelect={() => onSearch(stream.name)}
                  className="cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
                >
                  <StreamIcon className="text-slate-400" />
                  <span className="font-sans text-slate-700 dark:text-slate-200">{stream.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {user && matchedStreams.length > 0 && (
          <>
            <CommandSeparator className="bg-sand-200 dark:bg-slate-700" />
            <CommandGroup heading="My Streams">
              {matchedStreams.map((stream) => (
                <CommandItem
                  key={stream.id}
                  onSelect={() => { onClose(); window.open(MERCHANT_PORTAL_URL, '_blank') }}
                  className="cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
                >
                  <StreamIcon className="text-slate-400" />
                  <span className="font-sans text-slate-700 dark:text-slate-200">{stream.name}</span>
                  <CommandShortcut>↗</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {!value.trim() && (
          <>
            <CommandGroup heading={t('chrome.search.latestOffers')}>
              <CommandItem
                onSelect={() => { onClose(); navigate('/') }}
                className="cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
              >
                <Palmtree className="text-slate-400" />
                <span className="font-sans text-slate-700 dark:text-slate-200">{t('chrome.search.latestOffers')}</span>
              </CommandItem>
              <CommandItem
                onSelect={() => { onClose(); navigate('/categories') }}
                className="cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
              >
                <Tag className="text-slate-400" />
                <span className="font-sans text-slate-700 dark:text-slate-200">{t('chrome.search.browseCategories')}</span>
              </CommandItem>
            </CommandGroup>
            {user && (
              <>
                <CommandSeparator className="bg-sand-200 dark:bg-slate-700" />
                <CommandGroup heading={t('chrome.fab.open')}>
                  <CommandItem
                    onSelect={() => { onClose(); navigate('/account/drops') }}
                    className="cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
                  >
                    <Droplets className="text-slate-400" />
                    <span className="font-sans text-slate-700 dark:text-slate-200">{t('chrome.search.goToDropsBalance')}</span>
                  </CommandItem>
                  {isMerchant && hasStreams && (
                    <CommandItem
                      onSelect={() => { onClose(); window.open(`${MERCHANT_PORTAL_URL}/offers/new`, '_blank') }}
                      className="cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
                    >
                      <Tag className="text-slate-400" />
                      <span className="font-sans text-slate-700 dark:text-slate-200">{t('chrome.search.newOffer')}</span>
                      <CommandShortcut>{t('chrome.search.mbo')}</CommandShortcut>
                    </CommandItem>
                  )}
                  {isMerchant && (
                    <CommandItem
                      onSelect={() => { onClose(); window.open(`${MERCHANT_PORTAL_URL}/streams/new`, '_blank') }}
                      className="cursor-pointer data-[selected=true]:bg-sand-200 data-[selected=true]:text-slate-900 dark:data-[selected=true]:bg-slate-700 dark:data-[selected=true]:text-sand-100"
                    >
                      <StreamIcon className="text-slate-400" />
                      <span className="font-sans text-slate-700 dark:text-slate-200">{t('chrome.search.createStream')}</span>
                      <CommandShortcut>{t('chrome.search.merchant')}</CommandShortcut>
                    </CommandItem>
                  )}
                </CommandGroup>
              </>
            )}
          </>
        )}
      </CommandList>
      {inputAtBottom && input}
    </Command>
  )
}

// Radix requires DialogTitle + a description for screen readers even when the
// dialog has no visible heading — the search input itself is the visible UI.
function SearchDialogA11yLabel() {
  const { t } = useTranslation('navigation')
  return (
    <VisuallyHidden asChild>
      <div>
        <DialogTitle>{t('chrome.search.label')}</DialogTitle>
        <DialogDescription>{t('chrome.search.dialogDescription')}</DialogDescription>
      </div>
    </VisuallyHidden>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="8.5" cy="8.5" r="5" />
      <line x1="12.5" y1="12.5" x2="17" y2="17" />
    </svg>
  )
}

interface SearchTriggerProps {
  /** Controlled mode — used by mobile header to open the dialog externally */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function SearchTrigger({ open: externalOpen, onOpenChange }: SearchTriggerProps = {}) {
  const { t } = useTranslation('navigation')
  const [internalOpen, setInternalOpen] = useState(false)
  const [value, setValue] = useState('')
  const { navigate } = useLocaleNavigate()
  const { user, isMerchant } = useSession()
  const { data: myStreamsData } = useMyStreams()
  const myStreams = (myStreamsData?.workspaces ?? []).flatMap((ws) => ws.streams)
  const { suggestions } = useSearchSuggestions(value)

  // Fuzzy filter: stream name contains the search value (case-insensitive)
  const matchedStreams = value.trim()
    ? myStreams.filter((s) => s.name.toLowerCase().includes(value.trim().toLowerCase()))
    : myStreams

  const controlled = externalOpen !== undefined
  const open = controlled ? externalOpen : internalOpen
  const setOpen = controlled
    ? (onOpenChange ?? (() => {}))
    : setInternalOpen

  // S to open — only in uncontrolled (desktop) mode
  useEffect(() => {
    if (controlled) return
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (e.key === 's' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        setInternalOpen(true)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [controlled])

  function handleClose() {
    setOpen(false)
    setValue('')
  }

  // Mobile keyboard height — visualViewport shrinks when the on-screen
  // keyboard opens; fixed bottom-0 elements do NOT ride up with it on
  // modern Android Chrome (or iOS Safari at all), so the sheet's bottom
  // offset must track it manually. Transitioned so the sheet slides up
  // smoothly WITH the keyboard instead of jumping after it.
  const [keyboardOffset, setKeyboardOffset] = useState(0)
  useEffect(() => {
    if (!controlled || !open) { setKeyboardOffset(0); return }
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      setKeyboardOffset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop))
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [controlled, open])

  // In controlled (mobile) mode: bottom sheet, not centered dialog.
  // Input pinned to the bottom edge (against the keyboard), results
  // stack ABOVE it — inverse of the desktop palette's top-down layout.
  if (controlled) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
        <DialogContent
          className="overflow-hidden p-0 shadow-lg border border-sand-200 dark:border-slate-700 bg-sand-50 dark:bg-slate-900 [&>button]:hidden
            inset-x-0 bottom-0 top-auto left-0 translate-x-0 translate-y-0 w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom-8 data-[state=closed]:slide-out-to-bottom-8
            data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100
            data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0
            data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0
            transition-[bottom] duration-200 ease-out"
          style={{ bottom: keyboardOffset }}
        >
          <SearchDialogA11yLabel />
          <CommandPaletteContent
            value={value}
            setValue={setValue}
            onClose={handleClose}
            onSearch={handleSearch}
            navigate={navigate}
            user={user}
            isMerchant={isMerchant}
            matchedStreams={matchedStreams}
            hasStreams={myStreams.length > 0}
            suggestions={suggestions}
            inputAtBottom
          />
        </DialogContent>
      </Dialog>
    )
  }

  function handleSearch(q: string) {
    handleClose()
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search')
  }

  return (
    <>
      {/* ── Command palette ────────────────────────────────────────────────── */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
        <DialogContent className="overflow-hidden p-0 shadow-lg border border-sand-200 dark:border-slate-700 bg-sand-50 dark:bg-slate-900 sm:max-w-[550px] [&>button]:hidden !top-[12vh] !translate-y-0">
          <SearchDialogA11yLabel />
          <CommandPaletteContent
            value={value}
            setValue={setValue}
            onClose={handleClose}
            onSearch={handleSearch}
            navigate={navigate}
            user={user}
            isMerchant={isMerchant}
            matchedStreams={matchedStreams}
            hasStreams={myStreams.length > 0}
            suggestions={suggestions}
          />
        </DialogContent>
      </Dialog>

      {/* ── Circle button in right column ─────────────────────────────────── */}
      <div className="group relative mb-1 me-1">
        <span
          aria-hidden
          className="
            absolute end-full me-3 top-1/2 -translate-y-1/2 z-30
            flex items-center gap-1
            px-3 py-1.5 rounded-lg
            bg-slate-800 dark:bg-sand-100
            text-white dark:text-slate-800
            text-sm font-medium font-sans whitespace-nowrap
            opacity-0 group-hover:opacity-100
            pointer-events-none transition-opacity duration-150 shadow-sm
          "
        >
          {t('chrome.search.label')}
          <kbd className="ms-1 px-1.5 py-0.5 rounded border border-white/30 dark:border-slate-800/30 text-[9px] font-mono leading-none opacity-80">S</kbd>
        </span>

        <button
          onClick={() => setOpen(true)}
          aria-label={t('chrome.search.open')}
          className="
            w-10 h-10 rounded-full
            flex items-center justify-center
            bg-sand-100/80 dark:bg-slate-800/80
            shadow-[0_4px_12px_rgba(0,0,0,0.10)]
            hover:shadow-[0_4px_16px_rgba(0,0,0,0.16)]
            border-0
            text-slate-500 dark:text-slate-400
            hover:text-slate-700 dark:hover:text-sand-200
            transition-all backdrop-blur-sm
          "
        >
          <SearchIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </>
  )
}
